/*
 * What Settings, Agency, Workspaces says (LEA-476; docs/2026-11-02-lea-476-workspace-add-and-offboard-plan.md,
 * "Web"), decided apart from the screen: the answers of the add, remove and list routes parsed, and
 * one sentence per outcome. Pure, so every refusal can be checked without a page. The routes are
 * `workspace-add-routes.ts` and `workspace-offboard-routes.ts`; their codes are the vocabulary here.
 *
 * Copy names a workspace by the name Email Bison gave it and never shows a binding id. The removal
 * copy says what removing does and never promises that anything is erased: offboarding does not
 * erase conversations or drafts (plan, "What the evidence changed", 2).
 */
import { z } from "zod";
import { appCount } from "../../src/app-dates.ts";
import { type ImportState, ImportStateSchema } from "../../src/workspace-import.ts";
import { UNREACHABLE } from "../shell/account-copy.ts";

const ClassSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("new") }),
  z.object({ status: z.literal("added"), workspaceId: z.string(), hasKey: z.boolean() }),
  z.object({
    status: z.literal("offboarded"),
    workspaceId: z.string(),
    state: z.enum(["offboarding", "offboarded"]),
  }),
  z.object({ status: z.literal("other_organization") }),
]);
export const IdentifiedSchema = z.intersection(
  z.object({ providerWorkspaceId: z.number().int(), name: z.string() }),
  ClassSchema,
);
export type Identified = z.infer<typeof IdentifiedSchema>;

export const AddedSchema = z.object({
  outcome: z.enum(["added", "key_saved", "unchanged", "reonboarded"]),
  workspaceId: z.string(),
  providerWorkspaceId: z.number().int(),
  name: z.string(),
  keySaved: z.boolean(),
  keyError: z.string().optional(),
  enableByHand: z.array(z.string()).optional(),
});
export type Added = z.infer<typeof AddedSchema>;

const LeftSchema = z.object({
  waitingOnAnswers: z.number().int().nonnegative(),
  waitingOnQualification: z.number().int().nonnegative(),
  drafting: z.number().int().nonnegative(),
});
export type Left = z.infer<typeof LeftSchema>;

export const ManagedSchema = z.object({
  id: z.string(),
  name: z.string(),
  state: z.enum(["onboarded", "offboarding"]),
  hasKey: z.boolean().nullable(),
  campaigns: z.number().int().nonnegative(),
  running: z.boolean(),
  left: LeftSchema.nullable(),
  /** Whether its last 90 days of mail are still importing (LEA-483); an offboarding row is complete. */
  import: ImportStateSchema,
});
export type Managed = z.infer<typeof ManagedSchema>;

/** One route's answer: its status (0 for a failed network), its body, and `Retry-After` in seconds. */
export type Answer = {
  readonly status: number;
  readonly body: unknown;
  readonly retryAfter: string | null;
};

const codeOf = (body: unknown): string | null => {
  const parsed = z.object({ error: z.string() }).safeParse(body);
  return parsed.success ? parsed.data.error : null;
};

/** Where an Email Bison key comes from, in one line. A non-breaking hyphen keeps api-user whole. */
export const KEY_SOURCE =
  "In Email Bison, open the workspace's Settings, then Developer API, and make a New API Token of type api‑user. A key opens only the workspace it was made in.";

/** Said wherever a workspace added here is described: the flag is a developer's, not a switch. */
export const DRAFTING_STAYS_OFF = "Nothing is drafted until LeadCue's team turns drafting on.";

function waitMinutes(retryAfter: string | null): number {
  const seconds = retryAfter === null ? Number.NaN : Number(retryAfter);
  return Number.isFinite(seconds) && seconds > 0 ? Math.max(1, Math.ceil(seconds / 60)) : 1;
}

const KEY_REFUSALS: Readonly<Record<string, string>> = {
  not_configured:
    "Adding workspaces is not set up on this server yet. LeadCue's team can add it for you.",
  workspace_add_organization_refused:
    "Workspaces cannot be added to this agency here. LeadCue's team adds them.",
  workspace_owned_by_other_organization:
    "This workspace belongs to another LeadCue agency, so it cannot be added here.",
  integration_account_owned_elsewhere:
    "This workspace belongs to another LeadCue agency, so it cannot be added here.",
  workspace_configuration_conflict:
    "Someone changed this agency's workspaces at the same moment. Check the key again.",
  workspace_not_offboarded:
    "This workspace is still being removed. Resume its removal in the list, then add it back.",
  integration_workspace_mismatch:
    "This key now opens a different workspace from the one checked. Check the key again.",
};

/** Why a key could not be checked or a workspace not added, from the route's answer. */
export function keyRefusal(answer: Answer): string {
  const code = codeOf(answer.body);
  const known = code !== null && Object.hasOwn(KEY_REFUSALS, code) ? KEY_REFUSALS[code] : undefined;
  if (known !== undefined) return known;
  if (answer.status === 0) return UNREACHABLE;
  if (answer.status === 401) return "You were signed out. Reload the page to sign in again.";
  if (answer.status === 403) return "Only agency owners and admins add workspaces.";
  if (answer.status === 429) {
    const minutes = waitMinutes(answer.retryAfter);
    return `Too many keys tried. Try again in ${minutes === 1 ? "a minute" : `${minutes} minutes`}.`;
  }
  if (answer.status === 503) return "LeadCue cannot store keys right now. Try again later.";
  if (answer.status >= 500) return "LeadCue could not finish that. Try again.";
  return "Email Bison did not accept this key. Copy the whole key again, and check it is an api-user key made in that workspace.";
}

/** The step a checked key offers, or null when there is nothing to do with it here. */
export type KeyStep = { readonly label: string } | null;

/** What a checked key means for this agency, and the one step it offers. */
export function identifiedCopy(found: Identified): {
  readonly sentence: string;
  readonly step: KeyStep;
} {
  switch (found.status) {
    case "new":
      return {
        sentence: `Its replies start importing into the inbox once it is added. ${DRAFTING_STAYS_OFF}`,
        step: { label: "Add workspace" },
      };
    case "added":
      return found.hasKey
        ? { sentence: "It is already added, with its key. There is nothing to do.", step: null }
        : {
            sentence: "It is added, but without its key, so its replies are not importing.",
            step: { label: "Save key" },
          };
    case "offboarded":
      return found.state === "offboarding"
        ? {
            sentence:
              "It is still being removed. Resume its removal in the list below, then add it back.",
            step: null,
          }
        : {
            sentence: `It was removed earlier. Adding it back imports its replies again. ${DRAFTING_STAYS_OFF}`,
            step: { label: "Add it back" },
          };
    case "other_organization":
      return {
        sentence: "It belongs to another LeadCue agency, so it cannot be added here.",
        step: null,
      };
  }
}

/** What the section says once an add answered 200: the outcome, and whether its key still needs saving. */
export function addedCopy(added: Added): {
  readonly message: string;
  readonly keyMissing: boolean;
} {
  if (!added.keySaved)
    return {
      message: `${added.name} is added, but its key was not saved, so its replies are not importing yet.`,
      keyMissing: true,
    };
  const later = added.enableByHand?.length ?? 0;
  const message = {
    added: `${added.name} is added. Its replies are importing into the inbox. ${DRAFTING_STAYS_OFF}`,
    key_saved: `${added.name}'s key is saved. Its replies start importing into the inbox.`,
    unchanged: `${added.name} was already added with its key. Nothing changed.`,
    reonboarded: `${added.name} is back. Its replies are importing again. ${DRAFTING_STAYS_OFF}${
      later > 0 ? " LeadCue's team turns its earlier campaigns back on." : ""
    }`,
  }[added.outcome];
  return { message, keyMissing: false };
}

/** Why a removal (or its resume) was refused. */
export function removeRefusal(answer: Answer, name: string): string {
  const code = codeOf(answer.body);
  if (answer.status === 0) return UNREACHABLE;
  if (code === "confirm_name_mismatch")
    return `The name does not match. Type it as shown: ${name}.`;
  if (code === "offboarding_unavailable")
    return "Removing workspaces is not available on this server right now, so nothing changed. LeadCue's team can remove it.";
  if (answer.status === 403) return "Only agency owners and admins remove workspaces.";
  if (answer.status === 404) return `${name} is no longer in this agency. The list is up to date.`;
  if (answer.status === 409)
    return `${name} is already removed or being removed. The list is up to date.`;
  return `${name} was not removed. Try again.`;
}

/** What the section says once a removal started (202). */
export function removingCopy(name: string, state: string, resumed: boolean): string {
  if (state === "offboarded") return `${name} is removed.`;
  return resumed
    ? `Resuming the removal of ${name}. It leaves this list when it is done.`
    : `Removing ${name}. It leaves this list when it is done.`;
}

/**
 * The import a row shows, or null. A removal says what happens to the workspace, so it wins; a row
 * with no key stored imports nothing until one is, so it keeps "No key stored" and Add key rather
 * than promising an import that cannot move.
 */
export function rowImport(row: Managed): Exclude<ImportState, { state: "complete" }> | null {
  if (row.state !== "onboarded" || row.hasKey === false) return null;
  return row.import.state === "complete" ? null : row.import;
}

/** A row's state as a word with a tone: the `.lcr-state` vocabulary the campaigns list uses. */
export function rowState(row: Managed): {
  readonly label: string;
  readonly tone: "good" | "waiting" | "attention";
} {
  if (rowImport(row)) return { label: "Importing", tone: "waiting" };
  if (row.state === "onboarded") return { label: "Active", tone: "good" };
  return row.running
    ? { label: "Removing…", tone: "waiting" }
    : { label: "Stuck", tone: "attention" };
}

const plural = (count: number, one: string, many: string) =>
  `${appCount(count)} ${count === 1 ? one : many}`;

/** What a stuck removal still waits on, in one sentence. */
export function leftSentence(left: Left | null): string {
  if (left === null)
    return "Its removal stopped part way. LeadCue could not see what is still open.";
  const parts = [
    left.waitingOnQualification > 0 &&
      `${plural(left.waitingOnQualification, "reply waits", "replies wait")} on a lead's qualification`,
    left.waitingOnAnswers > 0 &&
      `${plural(left.waitingOnAnswers, "reply waits", "replies wait")} on a rep's answers`,
    left.drafting > 0 && `${plural(left.drafting, "reply is", "replies are")} still being drafted`,
  ].filter((part): part is string => typeof part === "string");
  if (parts.length === 0)
    return "Its removal stopped part way. Nothing is open now; resume it to finish.";
  return `Its removal stopped part way: ${parts.join(", ")}. Resume to try again; if it stops here again, LeadCue's team can close what is left.`;
}

/**
 * The row's second line: campaigns (while importing, how many are done of those it imports), then
 * whether a key is stored when the server can say.
 */
export function rowMeta(row: Managed): string {
  const importing = rowImport(row);
  const campaigns =
    importing === null
      ? plural(row.campaigns, "campaign", "campaigns")
      : importing.state === "finding"
        ? "Finding campaigns"
        : `${appCount(importing.done)} of ${plural(importing.total, "campaign", "campaigns")} done`;
  if (row.hasKey === null) return campaigns;
  return `${campaigns} · ${row.hasKey ? "Key stored" : "No key stored"}`;
}

/** The reason recorded with an in-app removal: the operator script asks for one, the app does not. */
export const REMOVE_REASON = "Removed in Settings by an agency owner or admin";
